import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, Mail, User, FileText } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/auth';
import { leads, type Lead } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface CreateLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LeadFormData {
  caller_name: string;
  caller_phone: string;
  caller_email: string;
  notes: string;
}

const initialFormData: LeadFormData = {
  caller_name: '',
  caller_phone: '',
  caller_email: '',
  notes: '',
};

export function CreateLeadModal({ open, onOpenChange }: CreateLeadModalProps) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [formData, setFormData] = useState<LeadFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof LeadFormData, string>>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<Lead>) => leads.create(token!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast({
        title: 'Lead stofnað',
        description: 'Nýr lead hefur verið stofnaður.',
        variant: 'success',
      });
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: 'Villa',
        description: error.message || 'Ekki tókst að stofna lead.',
        variant: 'destructive',
      });
    },
  });

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onOpenChange(false);
  };

  const handleChange = (field: keyof LeadFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof LeadFormData, string>> = {};

    if (!formData.caller_phone.trim()) {
      newErrors.caller_phone = 'Sími er skyldur';
    } else if (!/^[\d\s\-+()]+$/.test(formData.caller_phone)) {
      newErrors.caller_phone = 'Ógilt símanúmer';
    }

    if (formData.caller_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.caller_email)) {
      newErrors.caller_email = 'Ógilt netfang';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const leadData: Partial<Lead> = {
      caller_phone: formData.caller_phone.trim(),
      status: 'nyr',
      source: 'manual',
      lead_score: 50,
    };

    if (formData.caller_name.trim()) {
      leadData.caller_name = formData.caller_name.trim();
    }

    if (formData.caller_email.trim()) {
      leadData.caller_email = formData.caller_email.trim();
    }

    if (formData.notes.trim()) {
      leadData.notes = formData.notes.trim();
    }

    createMutation.mutate(leadData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Nýr lead</DialogTitle>
          <DialogDescription>
            Skráðu upplýsingar um nýjan lead. Sími er skyldur.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Caller Name */}
          <div>
            <label className="label flex items-center gap-1.5">
              <User className="w-4 h-4 text-gray-400" />
              Nafn
            </label>
            <input
              type="text"
              value={formData.caller_name}
              onChange={(e) => handleChange('caller_name', e.target.value)}
              className="input"
              placeholder="Nafn viðskiptavinar"
            />
          </div>

          {/* Caller Phone (required) */}
          <div>
            <label className="label flex items-center gap-1.5">
              <Phone className="w-4 h-4 text-gray-400" />
              Sími <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.caller_phone}
              onChange={(e) => handleChange('caller_phone', e.target.value)}
              className={`input ${errors.caller_phone ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="+354 555 1234"
              required
            />
            {errors.caller_phone && (
              <p className="text-red-500 text-sm mt-1">{errors.caller_phone}</p>
            )}
          </div>

          {/* Caller Email */}
          <div>
            <label className="label flex items-center gap-1.5">
              <Mail className="w-4 h-4 text-gray-400" />
              Netfang
            </label>
            <input
              type="email"
              value={formData.caller_email}
              onChange={(e) => handleChange('caller_email', e.target.value)}
              className={`input ${errors.caller_email ? 'border-red-500 focus:ring-red-500' : ''}`}
              placeholder="netfang@example.is"
            />
            {errors.caller_email && (
              <p className="text-red-500 text-sm mt-1">{errors.caller_email}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="label flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-gray-400" />
              Athugasemdir
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="input"
              rows={3}
              placeholder="Bæta við athugasemdum..."
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={createMutation.isPending}
            >
              Hætta við
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Stofna...' : 'Stofna lead'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
